import { useEffect, useRef, useState } from "react";
import PUB from "@/data/pub.json";
import { api, errMsg } from "@/lib/api";

export const PubDialog = ({ onClose, children, testid, wide = false }) => {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div className="dlg" role="dialog" aria-modal="true" data-testid={testid} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={wide ? "box wide" : "box"}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}><button className="btn xbtn" onClick={onClose} data-testid={`${testid}-close`}>Cerrar</button></div>
        {children}
      </div>
    </div>
  );
};

export const Scale = ({ value, onChange, testid }) => (
  <div className="scale" data-testid={testid}>
    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
      <button type="button" key={n} aria-pressed={value === n} onClick={() => onChange(n)} data-testid={`${testid}-${n}`}>{n}</button>
    ))}
  </div>
);

const Campo = ({ c, value, set }) => {
  const tid = `ficha-campo-${c.k}`;
  if (c.t === "scale") return <label>{c.l}<Scale value={value} onChange={set} testid={tid} /></label>;
  if (c.t === "choice") return (
    <label>{c.l}<div className="opt" data-testid={tid}>{c.o.map((o) => <button type="button" key={o} aria-pressed={value === o} onClick={() => set(o)}>{o}</button>)}</div></label>
  );
  if (c.t === "select") return (
    <label>{c.l}<select value={value || ""} onChange={(e) => set(e.target.value)} data-testid={tid}><option value="">Elige…</option>{c.o.map((o) => <option key={o}>{o}</option>)}</select></label>
  );
  return <label>{c.l}<input value={value || ""} onChange={(e) => set(e.target.value)} maxLength={300} data-testid={tid} /></label>;
};

const PHASES = [["Inhala", 4, 1.35], ["Sostén", 4, 1.35], ["Exhala", 6, 1]];

const Breath = () => {
  const circ = useRef(null);
  const timer = useRef(null);
  const [running, setRunning] = useState(false);
  useEffect(() => () => clearTimeout(timer.current), []);
  const start = () => {
    const c = circ.current;
    let n = 0, k = 0;
    setRunning(true);
    const step = () => {
      if (k >= 3) { n++; k = 0; }
      if (n >= 5) { c.textContent = "¡Bien!"; c.style.transform = "scale(1)"; setRunning(false); return; }
      const [t, s, sc] = PHASES[k];
      c.textContent = `${t} ${s}`; c.style.transitionDuration = `${s}s`; c.style.transform = `scale(${sc})`;
      k++;
      timer.current = setTimeout(step, s * 1000);
    };
    step();
  };
  return (
    <div className="breath" data-testid="breath-guide">
      <div className="bcirc" ref={circ} data-testid="breath-circle">Listo</div>
      <button className="btn btn-sol" onClick={start} disabled={running} style={{ marginTop: 12 }} data-testid="breath-start-btn">Iniciar respiración guiada (5 ciclos)</button>
    </div>
  );
};

function resultMsg(f, o) {
  let msg = "¡Registro guardado! ";
  if (f.prepost && o[f.prepost[0]] !== undefined && o[f.prepost[0]] !== "" && o[f.prepost[1]] !== undefined && o[f.prepost[1]] !== "") {
    const a = o[f.prepost[0]], b = o[f.prepost[1]];
    if (typeof a === "number") {
      const d = b - a;
      msg += f.menosEsMejor ? (d < 0 ? `Bajaste ${Math.abs(d)} puntos de tensión. ` : "Tu nivel no bajó esta vez: prueba otra técnica. ") : (d < 0 ? `Tu nivel bajó ${Math.abs(d)} puntos. ` : `Subiste ${d} puntos. `);
    } else msg += `Pasaste de ${a} a ${b}. `;
  }
  return msg + "El equipo de psicología revisará tu avance.";
}

const FichaForm = ({ f }) => {
  const [o, setO] = useState({ quien: "", grupo: "", edad: "" });
  const [ok, setOk] = useState(false);
  const [res, setRes] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setO((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const datos = { ...o, ok: true };
    f.campos.forEach((c) => { if (datos[c.k] === undefined) datos[c.k] = ""; });
    try {
      await api.post("/participaciones", { ficha: f.id, nombre: f.nombre, datos });
      setRes(resultMsg(f, datos));
    } catch (e2) { setErr(errMsg(e2)); setBusy(false); }
  };
  return (
    <>
      <h4 style={{ margin: "18px 0 0" }}>Registra tu práctica</h4>
      <p className="note">Usa tus iniciales o un código si prefieres. Si eres menor de edad, hazlo con autorización de tu acudiente.</p>
      <form className="frm" onSubmit={submit} data-testid="ficha-form">
        <label>Nombre o código<input value={o.quien} onChange={(e) => set("quien")(e.target.value)} maxLength={60} placeholder="Ej.: JP-12" data-testid="ficha-quien-input" /></label>
        <label>Deporte o grupo<input value={o.grupo} onChange={(e) => set("grupo")(e.target.value)} maxLength={60} data-testid="ficha-grupo-input" /></label>
        <label>Edad<select value={o.edad} onChange={(e) => set("edad")(e.target.value)} data-testid="ficha-edad-select"><option value="">Elige…</option>{["Menor de 12", "12 a 14", "15 a 17", "18 o más"].map((x) => <option key={x}>{x}</option>)}</select></label>
        {f.campos.map((c) => <Campo key={c.k} c={c} value={o[c.k]} set={set(c.k)} />)}
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 500 }}>
          <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} required style={{ width: "auto", minHeight: 0 }} data-testid="ficha-ok-checkbox" /> Acepto que la Fundación use estas respuestas de forma confidencial para acompañar mi proceso.
        </label>
        <button className="btn btn-sol" type="submit" disabled={busy || !!res} data-testid="ficha-submit-btn">Guardar mi registro</button>
      </form>
      {err && <p className="err" data-testid="ficha-error">{err}</p>}
      {res && <div className="res" data-testid="ficha-result">{res}</div>}
    </>
  );
};

const FichaDialog = ({ f, onClose }) => (
  <PubDialog onClose={onClose} testid="ficha-dialog">
    <small style={{ color: "var(--sol-l)", fontWeight: 800 }}>{f.deporte} · {f.habilidad}</small>
    <h3 style={{ margin: "4px 0" }} data-testid="ficha-dialog-title">{f.nombre}</h3>
    <p>{f.objetivo}</p>
    <div className="fhero">
      {f.video ? <video controls playsInline loop poster={f.img} data-testid="ficha-video"><source src={f.video} type="video/mp4" /></video> : <img src={f.img} alt="" />}
    </div>
    <div className="pasos">{f.pasos.map((p) => <div className="paso" key={p[0]}><b>{p[0]}</b><br />{p[1]}</div>)}</div>
    {f.guiada && <Breath />}
    <FichaForm f={f} />
  </PubDialog>
);

export const Fichas = () => {
  const [open, setOpen] = useState(null);
  return (
    <>
      <div className="figrid" data-testid="fichas-grid">
        {PUB.fichas.map((f, i) => (
          <button className="fcard" key={f.id} onClick={() => setOpen(i)} data-testid={`ficha-card-${f.id}`}>
            <img loading="lazy" src={f.img} alt="" />
            <div><small>{f.deporte} · {f.habilidad}</small><h4>{f.nombre}</h4><p>{f.objetivo}</p><span className="ftag">{f.video ? "▶ Video + " : ""}Practicar y registrar</span></div>
          </button>
        ))}
      </div>
      {open !== null && <FichaDialog f={PUB.fichas[open]} onClose={() => setOpen(null)} />}
    </>
  );
};
